import {
  computationTableSx,
  computationTableContainerSx,
} from "./computationTableStyles";
import { formatAmount } from "../../../../utils/hooks/dataHandler";
import React, { useMemo } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControlLabel,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Tooltip,
  IconButton,
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { JustInfo } from "../../../utils/Icon";
import {
  buildReview,
  detailedReviewRows,
  startupEligibilityLabel,
  yesNoLabel,
} from "../domain/review";
import { basisPeriod } from "../domain/preparation";
import RuleItem from "./RuleItem";
import BaseDialog from "../../../utils/dialog/BaseDialog";

const amount = (cell) =>
  cell && Number.isFinite(cell.value)
    ? `${cell.currency} ${formatAmount(cell.value, false)}`
    : "—";
const ExternalChip = () => (
  <Chip
    label='EXTERNAL'
    size='small'
    color='secondary'
    variant='outlined'
    sx={{ height: 18, fontSize: "0.6rem", fontWeight: 750 }}
  />
);
const rules = [
  [
    "Eligible companies",
    "Only companies that meet the applicable Group Relief eligibility requirements are considered.",
  ],
  [
    "Current-year items",
    "Only eligible current-year capital allowances, trade losses and donations are considered for transfer.",
  ],
  [
    "Qualifying period",
    "Transfers are subject to the applicable continuous qualifying period between the transferor and claimant.",
  ],
  [
    "Transfer limits",
    "Transfer amounts are limited by the available eligible items and the claimant's available assessable income.",
  ],
];

export default function ReviewStep({ workspace: w }) {
  const review = useMemo(
    () => buildReview(w.selected, w.settings),
    [w.selected, w.settings]
  );
  const rows = detailedReviewRows(review.companies);
  const basis = basisPeriod(w.settings.commonFinancialYearEnd);
  const edit = (step, companyId, field) => {
    w.setReviewOpen(false);
    w.setReviewFocusField(field || null);
    if (companyId) w.setActiveId(companyId);
    w.setStep(step);
  };
  const issues = [
    ...review.issues,
    ...w.selectionIssues.map((companyId) => ({
      companyId,
      step: 1,
      message: "Recheck this company's selection and access.",
    })),
  ];
  const close = () => {
    if (!w.optimising) w.setReviewOpen(false);
  };
  return (
    <BaseDialog
      open={w.reviewOpen}
      onClose={close}
      closeDisabled={w.optimising}
      title='Review and Calculate Group Relief'
      subtitle='Check your settings and confirm eligibility before calculating.'
      maxWidth='xl'
      paperSx={{
        m: { xs: 1, sm: 2 },
        width: { xs: "calc(100% - 16px)", sm: "calc(100% - 32px)" },
        maxHeight: "calc(100dvh - 32px)",
        overflow: "hidden",
      }}
      contentSx={{ p: { xs: 1.5, sm: 2 }, minHeight: 0 }}
      actionsSx={{ px: 2, py: 1, gap: 1, flexWrap: "wrap" }}
      actions={
        <>
          <Button onClick={close} disabled={w.optimising}>
            Back to companies
          </Button>
          {/* {w.result && (
            <Button
              disabled={w.optimising}
              onClick={() => {
                w.setReviewOpen(false);
                w.setShowResults(true);
                w.setStep(3);
              }}
            >
              {w.resultStale
                ? "View Previous Results (Out of Date)"
                : "View Results"}
            </Button>
          )} */}
          <Button
            variant='contained'
            disabled={
              Boolean(issues.length) ||
              !w.reviewEligibilityConfirmed ||
              !w.canContinue ||
              w.optimising
            }
            onClick={w.optimise}
          >
            {w.optimising ? "Calculating..." : "Calculate Group Relief"}
          </Button>
        </>
      }
    >
      <Stack spacing={2}>
        <Paper
          component='section'
          aria-labelledby='gro-review-settings-title'
          variant='outlined'
          sx={{ p: 2, borderRadius: 2 }}
        >
          <Stack
            direction='row'
            justifyContent='space-between'
            alignItems='center'
            gap={1}
          >
            <Typography
              id='gro-review-settings-title'
              variant='h6'
              fontWeight={750}
            >
              Review your settings
            </Typography>
            <Button disabled={w.optimising} onClick={() => edit(0)}>
              Edit case
            </Button>
          </Stack>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 1.5 }}>
            Your case settings, selected companies and checked figures from the
            previous steps are shown below.
          </Typography>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "stretch",
              border: 1,
              borderColor: "divider",
              borderRadius: 2,
              overflow: "hidden",
              mb: 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                bgcolor: "action.hover",
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                Assessment
              </Typography>
              <Typography variant='body2' fontWeight={750}>
                YA {w.settings.yearOfAssessment} · FYE{" "}
                {w.settings.commonFinancialYearEnd}
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                borderLeft: { xs: 0, sm: 1 },
                borderTop: { xs: 1, sm: 0 },
                borderColor: "divider",
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                Reference basis period
              </Typography>
              <Typography variant='body2' fontWeight={650}>
                {basis?.start} to {basis?.end} · {basis?.days} days
              </Typography>
            </Box>
            <Box
              sx={{
                flex: 1,
                px: 2,
                py: 1.5,
                borderLeft: { xs: 0, sm: 1 },
                borderTop: { xs: 1, sm: 0 },
                borderColor: "divider",
              }}
            >
              <Typography
                variant='caption'
                color='text.secondary'
                display='block'
              >
                Current YA rebate
              </Typography>
              <Typography variant='body2' fontWeight={650}>
                {w.settings.rebatePercentage}% · Cap: SGD{" "}
                {formatAmount(w.settings.rebateCap)}
              </Typography>
            </Box>
          </Box>
          <Typography
            variant='subtitle1'
            fontWeight={700}
            sx={{
              mt: 2,
              pt: 2,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            Detailed Tax Computation{" "}
            <Chip size='small' label='Before Group Relief' color='secondary' />
          </Typography>

          <Typography
            variant='body2'
            color='text.secondary'
            sx={{ mb: 2, lineHeight: 1.6 }}
          >
            Figures are based on the saved Main Tax Computation or uploaded
            external data and are presented separately for each YA. A dash (–)
            indicates that the item is not applicable or unavailable, and does
            not represent zero.
          </Typography>
          <TableContainer
            tabIndex={0}
            role='region'
            aria-label='Compare company computations'
            sx={computationTableContainerSx}
          >
            <Table
              stickyHeader
              size='small'
              aria-label='Detailed computation review'
              sx={{
                ...computationTableSx,
                "& tbody tr > :first-child": {
                  color: "text.primary",
                  fontWeight: 650,
                  borderRight: "2px solid",
                  borderRightColor: "divider",
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell scope='col' sx={{ fontWeight: 700 }}>
                    Description
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell
                      key={c.id}
                      scope='col'
                      align='right'
                      sx={{
                        minWidth: 160,
                        maxWidth: 200,
                        px: 1.5,
                        py: 1,
                      }}
                    >
                      <Stack
                        direction='row'
                        alignItems='center'
                        justifyContent='flex-end'
                        gap={0.5}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Stack
                            direction='row'
                            alignItems='center'
                            justifyContent='flex-end'
                            gap={0.5}
                          >
                            <Typography
                              variant='body2'
                              fontWeight={700}
                              noWrap
                              title={c.name}
                            >
                              {c.name}
                            </Typography>
                            {c.isExternal && <ExternalChip />}
                          </Stack>

                          <Typography
                            variant='caption'
                            color='text.secondary'
                            noWrap
                          >
                            {c.uen}{" "}
                            <Tooltip title='Review company'>
                              <IconButton
                                size='small'
                                disabled={w.optimising}
                                onClick={() => edit(2, c.id)}
                                aria-label={`Review ${c.name}`}
                              >
                                <VisibilityOutlinedIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRow>
                  <TableCell component='th' scope='row'>
                    Company / tax period type
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell key={c.id} align='right'>
                      {c.resolution?.companyType === "investment"
                        ? "Investment"
                        : "Trading" || "—"}{" "}
                      ·{" "}
                      {c.taxPeriodType === "cross_ya"
                        ? "Cross-YA"
                        : "Single YA" || "single_ya"}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell component='th' scope='row'>
                    Qualifies for SUTE
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell key={c.id} align='right'>
                      {startupEligibilityLabel(
                        c.isStartup,
                        c.taxPeriodType,
                        w.settings.yearOfAssessment
                      )}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell component='th' scope='row'>
                    75% Maintained Throughout
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell key={c.id} align='right'>
                      {yesNoLabel(c.shareholdingMaintainedThroughout)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell component='th' scope='row'>
                    75% Met on Last Day
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell key={c.id} align='right'>
                      {yesNoLabel(c.conditionMetOnFinalDay)}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell component='th' scope='row'>
                    Actual basis period
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell key={c.id} align='right'>
                      {c.financialYearStart} – {c.financialYearEnd}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell component='th' scope='row'>
                    Continuous Qualifying Days
                  </TableCell>
                  {review.companies.map((c) => (
                    <TableCell key={c.id} align='right'>
                      {c.continuousQualifyingDays || "Not confirmed"}
                    </TableCell>
                  ))}
                </TableRow>
                {rows.map((row) => {
                  const isTax = row.key.endsWith(":tax");
                  const isTotal = isTax || row.key.endsWith(":ci");
                  return (
                    <TableRow
                      key={row.key}
                      sx={
                        isTotal
                          ? {
                              "& > .MuiTableCell-root": {
                                fontWeight: 750,
                                color: isTax ? "primary.main" : "text.primary",
                                borderTop: "1px solid",
                                borderTopColor: "divider",
                                py: 1.5,
                                borderLeftColor: "primary.main",
                              },
                              "& > :first-child": {
                                borderLeft: "3px solid",
                                borderLeftColor: "primary.main",
                              },
                            }
                          : undefined
                      }
                    >
                      <TableCell component='th' scope='row'>
                        {row.label}
                      </TableCell>
                      {review.companies.map((c) => (
                        <TableCell
                          key={c.id}
                          align='right'
                          sx={
                            !Number.isFinite(row.cells[c.id]?.value)
                              ? { color: "text.secondary" }
                              : undefined
                          }
                        >
                          {amount(row.cells[c.id])}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
        <Paper variant='outlined' sx={{ p: 2.5, borderRadius: 2 }}>
          <Typography variant='h6' fontWeight={700} sx={{ mb: 1 }}>
            Please confirm that you have checked before proceeding:
          </Typography>

          <Stack spacing={1}>
            {rules.map(([label, info]) => (
              <RuleItem key={label} label={label} info={info} />
            ))}
          </Stack>

          {review.companies.find(
            (c) => c.resolution?.companyType === "investment"
          ) && (
            <Alert severity='warning' variant='outlined' sx={{ mt: 2 }}>
              <Typography variant='body2'>
                <strong>Investment holding companies:</strong> Additional
                restrictions may apply to transferable items.
                <JustInfo title='Excess investment expenses should not be treated as transferable trade losses. Eligible current-year donations and specified allowances may require separate review.' />
              </Typography>
            </Alert>
          )}
          <Box
            sx={{
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            <Box sx={{ width: "100%" }}>
              <FormControlLabel
                control={
                  <Checkbox
                    disabled={w.optimising}
                    checked={w.reviewEligibilityConfirmed}
                    onChange={(e) =>
                      w.setReviewEligibilityConfirmed(e.target.checked)
                    }
                  />
                }
                label={
                  <Typography variant='body2' fontWeight={600}>
                    I confirm that all companies and loss items qualify for
                    Group Relief and do not fall within exclusions or
                    limitations not handled by this tool. <br />
                    <Typography variant='caption' color='text.secondary'>
                      This tool does not guarantee that the claim is valid or
                      that the recommended allocation is optimal.
                    </Typography>
                  </Typography>
                }
              />

              <Alert
                severity={
                  !issues.length && w.reviewEligibilityConfirmed
                    ? "success"
                    : "info"
                }
                sx={{ mt: 2 }}
              >
                {!issues.length && w.reviewEligibilityConfirmed
                  ? "Eligibility confirmed. You can now calculate the Group Relief recommendations."
                  : issues.length
                    ? "Complete the outstanding review items before continuing."
                    : "Confirm eligibility to continue."}
              </Alert>

              {w.optimiseError && (
                <Alert severity='error' sx={{ mt: 2 }}>
                  {w.optimiseError}
                </Alert>
              )}
            </Box>
          </Box>
        </Paper>
      </Stack>
    </BaseDialog>
  );
}
